// src/components/PlanetRemediesPanel.jsx
import React, { useEffect, useMemo, useState } from "react";

const HINDI_PLANET = {
  Sun: "सूर्य",
  Moon: "चन्द्र",
  Mars: "मंगल",
  Mercury: "बुध",
  Jupiter: "गुरु",
  Venus: "शुक्र",
  Saturn: "शनि",
  Rahu: "राहु",
  Ketu: "केतु",
};

const ROLE_LABEL = {
  MD: "Mahadasha (life chapter)",
  AD: "Antardasha (current theme)",
  PD: "Pratyantar (day-to-day intensity)",
};

const ROLE_SHORT = {
  MD: "Mahadasha",
  AD: "Antardasha",
  PD: "Pratyantar",
};

const ROLE_COLORS = {
  MD: {
    bg: "linear-gradient(90deg,#f97316,#facc15)",
    border: "rgba(245,158,11,0.75)",
    text: "#111827",
  },
  AD: {
    bg: "linear-gradient(90deg,#22d3ee,#0ea5e9)",
    border: "rgba(34,211,238,0.75)",
    text: "#0f172a",
  },
  PD: {
    bg: "linear-gradient(90deg,#a855f7,#6366f1)",
    border: "rgba(168,85,247,0.75)",
    text: "#0f172a",
  },
};


const neutralChip = {
  background: "rgba(15,23,42,0.9)",
  border: "1px solid rgba(75,85,99,0.9)",
  color: "#e5e7eb",
};

function PlanetRemediesPanel({
  byPlanet = {},
  activeRoles = {},
  selectedPlanetExternal = null,
  onSelectedPlanetChange = null,
  plan = "STARTER",
}) {
  // =====================================================
  // 1. BASE DATA
  // =====================================================
  const rawPlanetNames = useMemo(() => Object.keys(byPlanet || {}), [byPlanet]);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const isTablet =
    typeof window !== "undefined" &&
    window.innerWidth >= 640 &&
    window.innerWidth < 1024;

  const isStarter = plan === "STARTER";
  const isLimited = isStarter; // ✅ Backend-driven

  // =====================================================
  // LIMITED rule: Starter shows only first 2 planets max
  // (but backend might return only 1 planet if MD/AD/PD same)
  // =====================================================
  const planetNames = useMemo(() => {
    if (!isLimited) return rawPlanetNames;
    return rawPlanetNames.slice(0, 2);
  }, [rawPlanetNames, isLimited]);

  
  
  // =====================================================
  // 2. INITIAL PLANET SELECTION
  // =====================================================
  const findInitialPlanet = () => {
    if (rawPlanetNames.length === 0) return "";

    const entries = Object.entries(activeRoles || {});
    const findByRole = (role) =>
      (entries.find(([, r]) => r === role) || [])[0];

    return (
      findByRole("AD") ||
      findByRole("MD") ||
      findByRole("PD") ||
      rawPlanetNames[0]
    );
  };

  const [selectedPlanetInternal, setSelectedPlanetInternal] =
    useState(findInitialPlanet);

  const selectedPlanet =
    selectedPlanetExternal &&
    rawPlanetNames.includes(selectedPlanetExternal)
      ? selectedPlanetExternal
      : selectedPlanetInternal;

  // =====================================================
  // 3. CURRENT PLANET DATA
  // =====================================================
  const current = byPlanet[selectedPlanet] || {};

  // =====================================================
  // 4. NORMALIZE BULLETS (IMPORTANT FIX)
  // =====================================================
  const normalizeBullets = (raw) => {
    if (!Array.isArray(raw)) return [];

    const texts = raw.map((item) => {
      if (typeof item === "string") return item;
      if (typeof item === "object" && item?.text) return item.text;
      return "";
    });

    const seen = new Set();
    return texts.filter((t) => {
      const key = t.trim();
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const primary = normalizeBullets(current.primary || current.behaviour);
  const secondary = normalizeBullets(current.secondary || current.service);
  const tertiary = normalizeBullets(current.tertiary || current.spiritual);

  // =====================================================
  // 5. KEEP SELECTION VALID
  // =====================================================
  useEffect(() => {
    if (!selectedPlanetInternal && rawPlanetNames.length > 0) {
      setSelectedPlanetInternal(rawPlanetNames[0]);
    }
  }, [rawPlanetNames, selectedPlanetInternal]);

  // ✅ Sync external selection → internal state
  useEffect(() => {
    if (
      selectedPlanetExternal &&
      rawPlanetNames.includes(selectedPlanetExternal)
    ) {
      setSelectedPlanetInternal(selectedPlanetExternal);
    }
  }, [selectedPlanetExternal, rawPlanetNames]);
  // =====================================================
  // 6. HELPERS
  // =====================================================
  const renderColumn = (title, bullets, roleColor) => {
    if (!bullets.length) return null;

    return (
      <div
        style={{
          padding: 16,
          borderRadius: 12,
          border: "1px solid rgba(148,163,184,0.14)",
          background:
            "linear-gradient(180deg, rgba(2,6,23,0.88), rgba(2,6,23,0.65))",
          borderTop: `3px solid ${roleColor || "rgba(75,85,99,0.6)"}`,
          boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
          transition: "all 0.2s ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            fontWeight: 700,
            fontSize: 13.5,
            color: "#e2e8f0",
            marginBottom: 10,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 6,
            letterSpacing: "0.02em",
          }}
        >
         <span> {title} </span>
        

          {isLimited && (
            <span style={{ fontSize: 10, color: "#64748b" }}>
              essential
            </span>
          )}
        </div> 
        <div
          style={{
            height: 1,
            background: "rgba(148,163,184,0.12)",
            marginBottom: 12,
          }}
        />
        {/* Bullets */}
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            fontSize: isMobile ? 12.5 : 13,
            color: "#e5e7eb",
            lineHeight: 1.75,
          }}
        >
          {bullets.map((text, i) => (
            <li key={i} style={{
                marginBottom: 9,
                lineHeight: 1.7,
              }}>
              {text}
            </li>
          ))}
        </ul>

        {/* Upgrade Hint */}
        {isLimited && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            Showing essential remedies. Full set unlocks in{" "}
            <span style={{ color: "rgba(251,191,36,0.7)" }}>Growth</span>.
          </div>
        )}
      </div>
    );
  };

  // =====================================================
  // 7. SAFETY
  // =====================================================
  if (!rawPlanetNames.length) {
    return (
      <div style={{ fontSize: 13, color: "#9ca3af" }}>
        No planet-wise remedies were generated.
      </div>
    );
  }

  const role = activeRoles[selectedPlanet] || null;

  const roleInfo = role
    ? ROLE_LABEL[role]
    : "Planet-wise balancing";

  const roleShort = role
    ? ROLE_SHORT[role]
    : null;

  // ✅ NEVER NULL (key fix)
  const colorCfg = ROLE_COLORS[role] || {
    border: "rgba(75,85,99,0.6)",
    bg: "rgba(2,6,23,0.65)",
    text: "#e5e7eb",
  };

  const hindiName = HINDI_PLANET[selectedPlanet] || "";
  const labelMain = selectedPlanet
    ? `${selectedPlanet}${hindiName ? ` (${hindiName})` : ""}`
    : "";

  const getChipStyle = (planet) => {
    const isSelected = planet === selectedPlanet;
    const role = activeRoles[planet] || null;

    const cfg = ROLE_COLORS[role] || {
      border: "rgba(75,85,99,0.6)",
      bg: "rgba(2,6,23,0.65)",
      text: "#e5e7eb",
    };

    // 🎯 Active + Selected
    if (cfg && isSelected) {
      return {
        padding: "6px 12px",
        borderRadius: 999,
        border: "none",
        background: cfg.bg,
        color: cfg.text,
        fontSize: 12,
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
        transform: "translateY(-1px) scale(1.03)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
      };
    }

    // 🎯 Active but not selected
    if (cfg && !isSelected) {
      return {
        padding: "6px 12px",
        borderRadius: 999,
        background: "transparent",
        color: "#e5e7eb",
        fontSize: 12,
        cursor: "pointer",
        whiteSpace: "nowrap",
        border: `1px solid ${cfg.border}`,
        opacity: 0.9,
        transition: "all 0.18s ease",
      };
    }

    // 🎯 Neutral
    return {
      ...neutralChip,
      padding: "6px 12px",
      borderRadius: 999,
      fontSize: 12,
      cursor: "pointer",
      whiteSpace: "nowrap",
      opacity: isSelected ? 1 : 0.8,
      transition: "all 0.18s ease",
    };
  };
  // =====================================================
  // 8. UI
  // =====================================================
  return (
    <div style={{ fontSize: 13 }}>
      {/* Starter Hint */}
      {isLimited && (
        <div
          style={{
            marginBottom: 10,
            fontSize: 11,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "#64748b",
          }}
        >
          Essential remedies — Starter plan
        </div>
      )}

      {/* Planet Selector */}
      <div style={{ 
        display: "flex", 
        gap: 10, 
        flexWrap: "wrap", 
        marginBottom: 14 
      }}>
        {planetNames.map((p) => {
          const role = activeRoles[p] || null;
          const hi = HINDI_PLANET[p];
          const cfg = role ? ROLE_COLORS[role] : null;

          return (
            <button
              key={p}
              onClick={() => {
                if (onSelectedPlanetChange) {
                  onSelectedPlanetChange(p);
                } else {
                  setSelectedPlanetInternal(p);
                }
              }}
              style={getChipStyle(p)}
            >
              {/* 🔵 Role Dot */}
              {cfg && (
                <span
                  style={{
                    display: "inline-block",
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: cfg.border,
                    marginRight: 6,
                    position: "relative",
                    top: -1,
                  }}
                />
              )}

              {/* Planet Name */}
              {p} {hi ? `(${hi})` : ""}

              {/* Role Label */}
              {role && (
                <span style={{ marginLeft: 6, opacity: 0.8 }}>
                  • {ROLE_SHORT[role]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Planet */}
      {selectedPlanet ? (
        <div
          style={{
            marginBottom: 12,
            marginTop: 6,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid rgba(148,163,184,0.16)",
            background: "rgba(15,23,42,0.55)",
            borderLeft: "4px solid rgba(245,158,11,0.85)",
            color: "#cbd5e1",
            fontSize: 12,
            lineHeight: 1.55,
          }}
        >
        Showing remedies for{" "}
        <span style={{ color: "#f8fafc", fontWeight: 800 }}>{labelMain}</span>
        {roleShort ? (
            <>
              {" "}
              <span style={{ color: "#94a3b8" }}>·</span>{" "}
              <span style={{ color: "#e5e7eb", fontWeight: 700 }}>
                {roleShort} — active now
              </span>
            </>
          ) : (
            <>
              {" "}
              <span style={{ color: "#94a3b8" }}>·</span>{" "}
              <span style={{ color: "#e5e7eb" }}>{roleInfo}</span>
            </>
          )}
        </div>
         ) : null}
    
      
      {/* Start Here guidance */}
      <div
        style={{
          marginBottom: 12,
          fontSize: 12,
          color: "#9ca3af",
          lineHeight: 1.6,
        }}
      >
        <i>
          Start with <strong style={{ color: "#e5e7eb" }}>Primary</strong>, then add
          Secondary; use Tertiary only if you feel called.
        </i>

        {isLimited && (
          <div style={{ marginTop: 6, color: "#64748b" }}>
            Deeper layers expand each category with more situational variations.
          </div>
        )}
      </div>

      {/* Columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : isTablet
            ? "1fr 1fr"
            : "1fr 1fr 1fr",
          gap: 14,
        }}
      >
        {renderColumn("🧠 Primary — Behaviour & mindset", primary, colorCfg?.border)}
        {renderColumn("🤝 Secondary — Service, charity & actions", secondary, colorCfg?.border)}
        {renderColumn("🕉️ Tertiary — Mantras & spiritual support", tertiary, colorCfg?.border)}
      </div>
    </div>
  );
}

export default PlanetRemediesPanel;